import React, { ReactNode } from "react";
import dashboardIcon from "../assets/img/icons/icon-dashboard.svg";
import getStartedIcon from "../assets/img/icons/icon-get-started.svg";
import peopleIcon from "../assets/img/icons/icon-people.svg";
import appointmentsIcon from "../assets/img/icons/icon-appointments.svg";
import plansIcon from "../assets/img/icons/icon-plans.svg";
import productsIcon from "../assets/img/icons/icon-products.svg";
import classesIcon from "../assets/img/icons/icon-classes.svg";
import moneyIcon from "../assets/img/icons/icon-money.svg";
import reportsIcon from "../assets/img/icons/icon-reports.svg";
import appsIcon from "../assets/img/icons/icon-apps.svg";
import settingsIcon from "../assets/img/icons/icon-settings.svg";
import communicateIcon from "../assets/img/icons/icon-send.svg";
import { AppsSidebar } from "../components/Sidebar/components/AppsSidebar";
import { ReportsSidebar } from "../components/Sidebar/components/ReportsSidebar";
import {
  APPOINTMENTS_FEATURE_FLAG,
  CALENDAR_FEATURE_FLAG,
  GET_STARTED_FEATURE_FLAG,
  PLAN_LIST_FEATURE_FLAG,
} from "../context/features/features.utils";
import { ACCOUNT_PAGE_TITLE } from "../pages/Account/AccountLanding/AccountLanding";

export type RouteChildren = {
  id?: string;
  name: string;
  path: string;
  reactPage?: boolean;
  featureRule?: string; // Only show if feature flag is true
  disabledFeatureRule?: string; // Only show if feature flag is false
  target?: string;
};

export type Route = {
  id: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  name: string;
  path: string;
  children?: RouteChildren[];
  dynamicChildren?: () => ReactNode;
  header?: boolean;
  badgeText?: string;
  badgeColor?: string;
  subPaths?: string[];
  featureRule?: string;
  disabledFeatureRule?: string;
  reactPage?: boolean;
};

// route feature rules don't work reliably here due to cache TTL
// so instead, inject the uncached feature flag value
// allowing users to toggle the feature flag and see the changes immediately
const routes = ({
  features,
}: {
  features: {
    isPeoplePageEnabled: boolean;
    isNewAccountPageEnabled: boolean;
  };
}): Route[] => {
  // Remove the feature flag after release: https://linear.app/pushpress/issue/GRWTH-652/remove-ssu-feature-flag-in-core
  const featureFlaggedAccountPageMenuItem: RouteChildren | null =
    features.isNewAccountPageEnabled
      ? {
          id: "nav-item-account",
          name: `${ACCOUNT_PAGE_TITLE} (Beta)`,
          path: "/account",
          reactPage: true,
        }
      : null;

  return [
    {
      id: "nav-menu-get-started",
      icon: getStartedIcon,
      name: "Get Started",
      path: "/start",
      featureRule: GET_STARTED_FEATURE_FLAG,
      reactPage: true,
    },
    {
      id: "nav-menu-dashboard",
      icon: dashboardIcon,
      name: "Dashboard",
      path: "/main",
      reactPage: true,
    },
    {
      id: "nav-menu-people",
      icon: peopleIcon,
      name: "People",
      path: features.isPeoplePageEnabled ? "/people/members" : "/members/index",
      reactPage: features.isPeoplePageEnabled ? true : false,
      children: features.isPeoplePageEnabled
        ? undefined
        : [
            {
              id: "nav-item-members",
              name: "Members",
              path: "/members/index",
            },
            {
              id: "nav-item-leads",
              name: "Leads",
              path: "/members/leads",
            },
            {
              id: "nav-item-staff-members",
              name: "Staff Members",
              path: "/users/index",
            },
          ],
      subPaths: features.isPeoplePageEnabled
        ? ["/people/leads", "/people/staff"]
        : [],
    },
    {
      id: "nav-menu-communicate",
      icon: communicateIcon,
      name: "Communicate",
      path: "/communications/automatic",
      children: [
        {
          id: "nav-item-communicate-automatic",
          name: "Automatic Emails",
          path: "/communications/automatic",
          reactPage: true,
        },
      ],
    },
    {
      id: "nav-menu-appointments",
      icon: appointmentsIcon,
      name: "Appointments",
      path: "/scheduler/index",
      children: [
        {
          id: "nav-item-appointments",
          name: "Appointments",
          path: "/scheduler/index",
          disabledFeatureRule: APPOINTMENTS_FEATURE_FLAG,
        },
        {
          id: "nav-item-appointment-types",
          name: "Appointment Types",
          path: "/scheduler/types",
          disabledFeatureRule: APPOINTMENTS_FEATURE_FLAG,
        },
        {
          id: "nav-item-appointments",
          reactPage: true,
          name: "Appointments",
          path: "/appointments",
        },
      ],
    },
    {
      id: "nav-menu-plans",
      icon: plansIcon,
      name: "Plans",
      path: "/plans/index",
      children: [
        {
          id: "nav-item-plans",
          name: "Plans",
          path: "/plans/index",
          disabledFeatureRule: PLAN_LIST_FEATURE_FLAG,
        },
        {
          id: "nav-item-plans",
          name: "Plans",
          path: "/plans/list",
          featureRule: PLAN_LIST_FEATURE_FLAG,
          reactPage: true,
        },
        {
          id: "nav-item-discounts",
          name: "Discounts",
          path: "/discounts/index/membership",
        },
      ],
    },
    {
      id: "nav-menu-products",
      icon: productsIcon,
      name: "Products",
      path: "/products/index",
      children: [
        {
          id: "nav-item-products",
          name: "Products",
          path: "/products/index",
        },
        {
          id: "nav-item-charge",
          name: "Charge A Guest",
          path: "/invoices/create#guest",
        },
        {
          id: "nav-item-product-categories",
          name: "Product Categories",
          path: "/products/categories",
        },
        {
          id: "nav-item-pre-orders",
          name: "Pre-Orders",
          path: "/products/preorders",
        },
        {
          id: "nav-item-product-discounts",
          name: "Discounts",
          path: "/discounts/index/product",
        },
      ],
    },
    {
      id: "nav-menu-calendar",
      icon: classesIcon,
      name: "Schedule",
      path: "/schedule/index",
      children: [
        {
          id: "nav-item-schedule",
          name: "Calendar",
          path: "/schedule",
          reactPage: true,
          featureRule: CALENDAR_FEATURE_FLAG,
        },
        {
          id: "nav-item-classes",
          name: "Classes",
          path: "/schedule/classes",
          reactPage: true,
          featureRule: CALENDAR_FEATURE_FLAG,
        },
        {
          id: "nav-item-events",
          name: "Events",
          path: "/schedule/events",
          reactPage: true,
          featureRule: CALENDAR_FEATURE_FLAG,
        },
        {
          id: "nav-item-checkins",
          name: "Check-Ins",
          path: "/calendar/checkins",
        },
        {
          name: "Calendar",
          path: "/calendar/index",
          disabledFeatureRule: CALENDAR_FEATURE_FLAG,
        },
        {
          name: "Events",
          path: "/calendar/events",
          disabledFeatureRule: CALENDAR_FEATURE_FLAG,
        },
      ],
      subPaths: ["/locations"],
    },
    {
      id: "nav-menu-payments",
      icon: moneyIcon,
      name: "Payments",
      path: "/reports/payments",
      children: [
        {
          id: "nav-item-invoices",
          name: "Invoices",
          path: "/invoices",
        },
        {
          id: "nav-item-transactions",
          name: "Transactions",
          path: "/reports/transactions",
        },
        {
          id: "nav-item-payments",
          name: "Payments Forecast",
          path: "/reports/payments",
        },
        {
          id: "nav-item-deposits",
          name: "Daily Deposits",
          path: "/reports/deposits",
        },
      ],
    },
    {
      id: "nav-menu-reports",
      icon: reportsIcon,
      name: "Reports",
      path: "/reports/index",
      children: [
        {
          id: "nav-item-sales-report",
          name: "Product Sales Report",
          path: "/reports/retail",
        },
        {
          id: "nav-item-payroll-legacy-report",
          name: "Payroll",
          path: "/reporting/export-payroll",
        },
        {
          id: "nav-item-no-show-",
          name: "No Show / Late Cancel",
          path: "/no-show",
          reactPage: true,
        },
        {
          id: "celebrations-report",
          name: "Celebrations",
          path: "/reports/celebrations",
          reactPage: true,
        },
      ],
      dynamicChildren: () => <ReportsSidebar />,
    },
    {
      icon: appsIcon,
      name: "Apps",
      id: "nav-menu-apps",
      path: "/app-store",
      children: [],
      dynamicChildren: () => <AppsSidebar />,
    },
    {
      icon: settingsIcon,
      name: "Settings",
      id: "nav-menu-settings",
      path: "/settings/index",
      children: [
        ...(featureFlaggedAccountPageMenuItem
          ? [featureFlaggedAccountPageMenuItem]
          : []),
        {
          id: "nav-item-billling",
          name: "Your PushPress Plan",
          path: "/settings/account",
        },
        {
          id: "nav-item-settings",
          name: "Settings",
          path: "/settings/index",
        },
        {
          id: "nav-item-bank",
          name: "Bank Connection",
          path: "/settings/bank",
        },
        {
          id: "nav-item-lead-capture",
          name: "Lead Capture",
          path: "/settings/leads",
        },
        {
          id: "nav-item-migration",
          name: "Migration Assistant",
          path: "/migration-assistant/",
          reactPage: true,
        },
        {
          id: "nav-item-policies",
          name: "Policies",
          path: "/settings/policy",
        },
      ],
    },
  ];
};

export default routes;
